import {Injectable} from "@angular/core";
import {environment} from "../../environments/environment";
import posthog from 'posthog-js'

@Injectable({
  providedIn: "root",
})
/**
 * The `TrackingService` class provides a mechanism to track user events and interactions
 * within the application. It integrates with third-party analytics tools such as PostHog
 * to record events and provides functionality to handle additional event metadata.
 *
 * The service is designed to be used as a singleton and is provided at the root level
 * of the Angular dependency injection system.
 *
 * Functionality within this service includes:
 * - Initialization of PostHog analytics (when in production mode).
 * - Recording of events and their respective metadata.
 *
 * Dependencies such as environment variables and PostHog configuration are required.
 *
 * The service internally manages state to ensure controlled execution of analytics processes.
 */
export class TrackingService {
  private tracked: Set<string> = new Set();

  constructor() {
    if (environment.production) {
      this.initPosthog();
    }
  }

  /**
   * Initializes the PostHog analytics library with the specified configuration.
   * Sets up tracking for anonymous user profiles and applies default settings.
   * Logs an error to the console if initialization fails.
   * Updates the tracked set to include "posthog" upon successful initialization.
   *
   * @return {void} No value is returned.
   */
  private initPosthog(): void {
    try {
      posthog.init(
        environment.POSTHOG_KEY,
        {
          api_host: environment.POSTHOG_HOST,
          person_profiles: "always", // Creates profiles for anonymous users
          defaults: '2025-05-24'
        }
      )
    } catch (e) {
      console.error("Posthog initialization failed: ", e)
    }

    this.tracked.add("posthog");
  }


  /**
   * Records an event with optional additional data for tracking purposes.
   *
   * @param {string} event - The name of the event to be recorded.
   * @param {Object} [additional] - Optional additional data to include with the event. Keys should be strings, and values can be strings, numbers, or booleans.
   * @return {void}
   */
  recordEvent(event: string, additional?: { [a: string]: string | number | boolean }): void {
    if (this.tracked.has("posthog")) {
      posthog.capture(event, additional);
    }
  }
}
